<template>
  <v-container class="checkin-container">
    <!-- Completion Screen -->
    <div v-if="showCompletion" class="completion-screen">
      <v-row class="text-center">
        <v-col cols="12">
          <h1>Je bent al aan het bewegen</h1>
          <span class="subtitle">Opmerken is genoeg.</span>
        </v-col>
        <v-col cols="12" class="mt-8">
          <v-btn color="accent" block class="mb-4" @click="resetCheckin">
            Nog een check-in
          </v-btn>
          <v-btn text block @click="$router.go(-1)">
            Klaar
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Screen 1: Waar ben je nu? -->
    <transition name="fade">
      <div v-if="currentScreen === 1 && !showCompletion" key="screen1">
        <v-row class="text-center">
          <v-col cols="12" class="progress-dots">
            <span class="dot active">1</span>
            <span class="dot-line"></span>
            <span class="dot">2</span>
            <span class="dot-line"></span>
            <span class="dot">3</span>
          </v-col>

          <v-col cols="12">
            <h1>Bewust worden</h1>
            <h3>Waar ben je nu?</h3>
            <span class="subtitle">Sta even stil. Wat merk je op in jezelf?</span>
          </v-col>

          <v-col cols="12" class="mb-2" @click="selectState('ventral')">
            <v-card
                class="checkin-card"
                :class="{ selected: selections.state === 'ventral' }"
                :style="selections.state === 'ventral' ? { borderColor: '#6FAF8F', backgroundColor: 'rgba(111,175,143,0.15)' } : {}"
            >
              <v-card-text>
                <div class="card-title">Rust & verbinding</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" class="mb-2" @click="selectState('sympathetic')">
            <v-card
                class="checkin-card"
                :class="{ selected: selections.state === 'sympathetic' }"
                :style="selections.state === 'sympathetic' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
            >
              <v-card-text>
                <div class="card-title">Onrust / spanning</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" class="mb-2" @click="selectState('dorsal')">
            <v-card
                class="checkin-card"
                :class="{ selected: selections.state === 'dorsal' }"
                :style="selections.state === 'dorsal' ? { borderColor: '#B9B9B9', backgroundColor: 'rgba(185,185,185,0.15)' } : {}"
            >
              <v-card-text>
                <div class="card-title">Moe / afgesloten</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <span class="helper-text">Er is geen goed of fout. Alleen opmerken.</span>
          </v-col>

          <v-col cols="12" class="button-container">
            <v-btn
                color="accent"
                block
                height="52"
                @click="handleNext"
                :disabled="selections.state === null"
            >
              Volgende
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </transition>

    <!-- Screen 2: Wat speelt er in jou? -->
    <transition name="fade">
      <div v-if="currentScreen === 2 && !showCompletion" key="screen2">
        <v-row class="text-center">
          <v-col cols="12" class="progress-dots-col">
            <div class="progress-dots">
              <span class="dot done clickable" @click="currentScreen = 1">
                <v-icon small>fa fa-check</v-icon>
              </span>
              <span class="dot-line"></span>
              <span class="dot active">2</span>
              <span class="dot-line"></span>
              <span class="dot">3</span>
            </div>
          </v-col>

          <v-col cols="12">
            <h1>Begrijpen wat gebeurt</h1>
            <h3>Wat speelt er in jou?</h3>
            <span class="subtitle">Je systeem probeert je te helpen.</span>
          </v-col>

          <v-col cols="12" @click="selectPart('self')">
            <v-card
                class="checkin-card"
                :class="{ selected: selections.part === 'self' }"
                :style="selections.part === 'self' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
            >
              <v-card-text>
                <div class="card-title">🤝 Gezonde Zelf</div>
                <div class="card-body">Rust, overzicht</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" @click="selectPart('survival')">
            <v-card
                class="checkin-card"
                :class="{ selected: selections.part === 'survival' }"
                :style="selections.part === 'survival' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
            >
              <v-card-text>
                <div class="card-title">🛡️ Overlevingsdeel</div>
                <div class="card-body">aanpassen, doorgaan, controle</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" @click="selectPart('pain')">
            <v-card
                class="checkin-card"
                :class="{ selected: selections.part === 'pain' }"
                :style="selections.part === 'pain' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
            >
              <v-card-text>
                <div class="card-title">💔 Pijndeel</div>
                <div class="card-body">verdriet, angst, oude pijn</div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12">
            <span class="helper-text">Herken je een patroon? Bijvoorbeeld pleasen, haasten of sterk zijn.</span>
          </v-col>

          <v-col cols="12" class="button-container">
            <v-btn
                color="accent"
                block
                height="52"
                @click="handleNext"
                :disabled="selections.part === null"
            >
              Wat heb ik nodig?
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </transition>

    <!-- Screen 3: Wat heb je nu nodig? -->
    <transition name="fade">
      <div v-if="currentScreen === 3 && !showCompletion" key="screen3">
        <v-row class="text-center">
          <v-col cols="12" class="progress-dots-col">
            <div class="progress-dots">
              <span class="dot done clickable" @click="currentScreen = 1">
                <v-icon small>fa fa-check</v-icon>
              </span>
              <span class="dot-line"></span>
              <span class="dot done clickable" @click="currentScreen = 2">
                <v-icon small>fa fa-check</v-icon>
              </span>
              <span class="dot-line"></span>
              <span class="dot active">3</span>
            </div>
          </v-col>

          <v-col cols="12" v-if="selections.need === null">
            <h1>Reguleren & Kiezen</h1>
            <h3>Wat heb je nu nodig?</h3>
            <span class="subtitle">Kies één kleine stap.</span>
          </v-col>

          <!-- Cards (hidden when need selected) -->
          <transition name="fade">
            <div v-if="selections.need === null" class="cards-container">
              <v-col cols="12" class="mb-4" @click="selectNeed('rest')">
                <v-card
                    class="checkin-card"
                    :class="{ selected: selections.need === 'rest' }"
                    :style="selections.need === 'rest' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
                >
                  <v-card-text>
                    <div class="card-title">🌬️ Rust</div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" class="mb-4" @click="selectNeed('movement')">
                <v-card
                    class="checkin-card"
                    :class="{ selected: selections.need === 'movement' }"
                    :style="selections.need === 'movement' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
                >
                  <v-card-text>
                    <div class="card-title">👣 Beweging</div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" class="mb-4" @click="selectNeed('orientation')">
                <v-card
                    class="checkin-card"
                    :class="{ selected: selections.need === 'orientation' }"
                    :style="selections.need === 'orientation' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
                >
                  <v-card-text>
                    <div class="card-title">👀 Oriëntatie</div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" class="mb-4" @click="selectNeed('contact')">
                <v-card
                    class="checkin-card"
                    :class="{ selected: selections.need === 'contact' }"
                    :style="selections.need === 'contact' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
                >
                  <v-card-text>
                    <div class="card-title">💬 Contact</div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" class="mb-2" @click="selectNeed('boundary')">
                <v-card
                    class="checkin-card"
                    :class="{ selected: selections.need === 'boundary' }"
                    :style="selections.need === 'boundary' ? { borderColor: '#F08A00', backgroundColor: 'rgba(240,138,0,0.15)' } : {}"
                >
                  <v-card-text>
                    <div class="card-title">✋ Begrenzen</div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" class="mb-2">
                <span class="helper-text">Klein is genoeg.</span>
              </v-col>
            </div>
          </transition>

          <v-col cols="12">
            <!-- Breathing Circle (overlays cards when active) -->
            <h1 v-if="selections.need === 'rest'">Rust</h1>
            <h3 v-if="selections.need === 'rest'">breng je adem tot rust</h3>

            <h1 v-if="selections.need === 'movement'">Bewegen</h1>
            <h3 v-if="selections.need === 'movement'">breng je lichaam in actie</h3>

            <h1 v-if="selections.need === 'orientation'">Oriëntatie</h1>
            <h3 v-if="selections.need === 'orientation'">Kom terug in het moment</h3>

            <h1 v-if="selections.need === 'contact'">Contact</h1>
            <h3 v-if="selections.need === 'contact'">Maak even verbinding</h3>

            <h1 v-if="selections.need === 'boundary'">Begrenzen</h1>
            <h3 v-if="selections.need === 'boundary'">Zeg wat je nodig hebt</h3>
            <transition name="fade">
              <div v-if="selections.need === 'rest'" class="breathing-wrapper">
                <div class="breathing-container">
                  <div class="breathing-circle"></div>
                  <transition name="fadeText" mode="out-in">
                    <p :key="breathingPhase" class="breathing-text">{{ breathingPhase }}</p>
                  </transition>
                </div>
              </div>

              <div v-if="selections.need === 'movement'">
                <div class="fading-text-container" style="--item-count: 4;">
                  <div class="step-text">
                    Sta even op
                  </div>
                  <div class="step-text">
                    Beweeg je schouders
                  </div>
                  <div class="step-text">
                    Schud je armen los
                  </div>
                  <div class="step-text">
                    Loop een paar stappen
                  </div>
                </div>
              </div>

              <div v-if="selections.need === 'orientation'">
                <div class="fading-text-container" style="--item-count: 3;">
                  <div class="step-text">
                    Kijk rustig om je heen
                  </div>
                  <div class="step-text">
                    Noem 3 dingen die je ziet
                  </div>
                  <div class="step-text">
                    Voel je voeten op de grond
                  </div>
                </div>
              </div>

              <div v-if="selections.need === 'contact'">
                <div class="fading-text-container" style="--item-count: 3;">
                  <div class="step-text">
                    Bel iemand
                  </div>
                  <div class="step-text">
                    Stuur een bericht
                  </div>
                  <div class="step-text">
                    Deel wat er speelt
                  </div>
                </div>
              </div>

              <div v-if="selections.need === 'boundary'">
                <div class="fading-text-container" style="--item-count: 2;">
                  <div class="step-text">
                    Wat heb jij nu nodig?
                  </div>
                  <div class="step-text">
                    Kun je dat uitspreken?
                  </div>
                </div>
              </div>
            </transition>
          </v-col>

          <v-col cols="12" class="button-container">
            <v-btn
                color="accent"
                block
                height="52"
                @click="submitCheckin"
                :disabled="selections.need === null || isLoading"
                :loading="isLoading"
            >
              Afronden
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </transition>

    <snackbar-text :text="error.message" bg-color="red" text-color="white" v-model="error.show"/>
  </v-container>
</template>

<script>
import apiClient from '@/services/apiClient';
import SnackbarText from '@/components/snackbar-text';

export default {
  name: 'HumanwiseCheckin',
  components: {SnackbarText},
  data: () => ({
    currentScreen: 1,
    selections: {
      state: null,
      part: null,
      need: null,
    },
    checkinUuid: null,
    breathingActive: false,
    breathingTimer: null,
    breathingPhaseTimer: null,
    breathingPhase: 'Adem in',
    isLoading: false,
    showCompletion: false,
    error: {
      show: false,
      message: '',
    },
  }),
  mounted() {
    this.loadCurrentCheckin();
  },
  beforeDestroy() {
    this.stopBreathing();
  },
  methods: {
    loadCurrentCheckin() {
      apiClient.get('checkin/current').then((response) => {
        const checkin = response.data;

        if (checkin && Object.keys(checkin).length > 0 && checkin.status !== 'completed') {
          this.checkinUuid = checkin.uuid;
          this.selections = {
            state: checkin.state,
            part: checkin.part,
            need: checkin.need,
          };

          if (checkin.need) {
            this.currentScreen = 3;
            if (checkin.need === 'rest') {
              this.startBreathing();
            }
          } else if (checkin.part) {
            this.currentScreen = 2;
          } else if (checkin.state) {
            this.currentScreen = 1;
          }
        }
      }).catch((err) => {
        console.log('No current checkin found or error:', err.status);
      });
    },
    selectState(value) {
      this.$set(this.selections, 'state', value);
      this.pushStepToAPI();
    },
    selectPart(value) {
      this.$set(this.selections, 'part', value);
      this.pushStepToAPI();
    },
    selectNeed(value) {
      this.$set(this.selections, 'need', value);
      this.pushStepToAPI();
      if (value === 'rest') {
        this.startBreathing();
      }
    },
    handleNext() {
      if (this.currentScreen === 1 && this.selections.state === null) return;
      if (this.currentScreen === 2 && this.selections.part === null) return;
      if (this.currentScreen === 3 && this.selections.need === null) {
        this.submitCheckin();
        return;
      }
      this.currentScreen += 1;
    },
    pushStepToAPI() {
      const payload = {
        state: this.selections.state,
        part: this.selections.part,
        need: this.selections.need,
        completed: false,
      };

      if (!this.checkinUuid) {
        apiClient.post('checkin', payload).then((response) => {
          this.checkinUuid = response.data.uuid;
        }).catch((err) => {
          console.error('Error creating checkin:', err);
        });
      } else {
        apiClient.patch(`checkin/${this.checkinUuid}`, payload).catch((err) => {
          console.error('Error updating checkin:', err);
        });
      }
    },
    startBreathing() {
      this.breathingActive = true;
      this.breathingPhase = 'Adem in';

      // Maak vorige timers schoon
      if (this.breathingTimer) {
        clearTimeout(this.breathingTimer);
      }
      if (this.breathingPhaseTimer) {
        clearInterval(this.breathingPhaseTimer);
      }

      const cyclus = () => {
        // Adem in - 4000ms
        this.breathingPhase = 'Adem in';

        this.breathingTimer = setTimeout(() => {
          // Adem uit - 6000ms
          this.breathingPhase = 'Adem uit';

          this.breathingTimer = setTimeout(() => {
            if (this.breathingActive) {
              cyclus(); // Start volgende cyclus
            }
          }, 6000);
        }, 4000);
      };

      cyclus(); // Start eerste cyclus
    },

    stopBreathing() {
      this.breathingActive = false;
      if (this.breathingTimer) {
        clearTimeout(this.breathingTimer);
      }
      if (this.breathingPhaseTimer) {
        clearInterval(this.breathingPhaseTimer);
      }
    },
    submitCheckin() {
      this.isLoading = true;
      const payload = {
        state: this.selections.state,
        part: this.selections.part,
        need: this.selections.need,
        completed: true,
        status: 'completed',
      };

      let request;
      if (this.checkinUuid) {
        request = apiClient.patch(`checkin/${this.checkinUuid}`, payload);
      } else {
        request = apiClient.post('checkin', payload);
      }

      request.then(() => {
        this.showCompletion = true;
        this.isLoading = false;
      }).catch((err) => {
        this.error.message = 'Er is een fout opgetreden. Probeer het later opnieuw.';
        this.error.show = true;
        this.isLoading = false;
        console.error('Error submitting checkin:', err);
      });
    },
    resetCheckin() {
      this.currentScreen = 1;
      this.selections = {state: null, part: null, need: null};
      this.checkinUuid = null;
      this.showCompletion = false;

      this.stopBreathing();
    },
  },
};
</script>

<style scoped>
.checkin-container {
  max-width: 480px;
  margin: 0 auto;
  padding-bottom: 40px;
}

h1 {
  font-size: 28px;
  font-weight: bold;
  color: white;
}

.subtitle {
  font-size: 16px;
  color: #E0E0E0;
  margin-bottom: 20px;
}

.helper-text {
  font-size: 14px;
  color: #D0D0D0;
}

.explainer-text {
  font-size: 14px;
  color: #7A7A7A;
  margin-top: 8px;
  transition: opacity 0.2s ease;
}

.v-col {
  cursor: pointer;
}

.checkin-card {
  transition: all 0.2s ease;
  border: 2px solid transparent;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 15px;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkin-card:hover {
  transform: scale(1.02);
}

.checkin-card.selected {
  border-color: #F08A00;
  background-color: rgba(240, 138, 0, 0.15);
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.progress-dots-col {
  display: flex;
  justify-content: center;
  align-items: center;
}

.progress-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: #7A7A7A;
}

.dot.active {
  background-color: #F08A00;
  color: white;
}

.dot.done {
  background-color: #F08A00;
  color: white;
}

.dot.clickable {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.dot.clickable:hover {
  transform: scale(1.1);
}

.dot-line {
  width: 20px;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.1);
}

.back-link {
  cursor: pointer;
  color: #F08A00;
  font-size: 14px;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.back-link:hover {
  opacity: 0.8;
}

.breathing-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  min-height: 400px;
}

.breathing-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
}

.breathing-circle {
  width: 120px;
  height: 120px;
  border: 5px solid #6FAF8F;
  border-radius: 50%;
  animation: breathe 10s ease-in-out infinite;
}

@keyframes breathe {
  0% {
    width: 80px;
    height: 80px;
    opacity: 0.4;
  }
  40% {
    width: 150px;
    height: 150px;
    opacity: 1;
  }
  50% {
    width: 150px;
    height: 150px;
    opacity: 1;
  }
  90% {
    width: 80px;
    height: 80px;
    opacity: 0.4;
  }
  100% {
    width: 80px;
    height: 80px;
    opacity: 0.4;
  }
}

.breathing-text {
  font-size: 14px;
  color: #D0D0D0;
  text-align: center;
  min-height: 20px;
  transition: opacity 0.5s ease-in-out;
}

.cards-container {
  width: 100%;
}

.completion-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.button-container {
  margin-top: 0px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fadeText-enter-active,
.fadeText-leave-active {
  transition: opacity 0.4s ease;
}

.fadeText-enter,
.fadeText-leave-to {
  opacity: 0;
}

.fading-text-container {
  text-align: center;
  padding: 2rem 1rem;
}

.fading-text-container .step-text {
  font-size: 18px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 1.5rem;
  min-height: 30px;
  opacity: 0;
}

@keyframes step-cycle-2 {
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  60% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

@keyframes step-cycle-3 {
  0% {
    opacity: 0;
  }
  6.67% {
    opacity: 1;
  }
  33.33% {
    opacity: 1;
  }
  40% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

@keyframes step-cycle-4 {
  0% {
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  25% {
    opacity: 1;
  }
  30% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

@keyframes step-cycle-5 {
  0% {
    opacity: 0;
  }
  4% {
    opacity: 1;
  }
  20% {
    opacity: 1;
  }
  24% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

@keyframes step-cycle-6 {
  0% {
    opacity: 0;
  }
  3.33% {
    opacity: 1;
  }
  16.67% {
    opacity: 1;
  }
  20% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.fading-text-container {
  --item-count: 4;
  --item-duration: 4s;
  --total-duration: calc(var(--item-count) * var(--item-duration));
}

.fading-text-container[style*="--item-count: 2"] .step-text {
  animation: step-cycle-2 var(--total-duration) linear infinite;
}

.fading-text-container[style*="--item-count: 3"] .step-text {
  animation: step-cycle-3 var(--total-duration) linear infinite;
}

.fading-text-container[style*="--item-count: 4"] .step-text {
  animation: step-cycle-4 var(--total-duration) linear infinite;
}

.fading-text-container[style*="--item-count: 5"] .step-text {
  animation: step-cycle-5 var(--total-duration) linear infinite;
}

.fading-text-container[style*="--item-count: 6"] .step-text {
  animation: step-cycle-6 var(--total-duration) linear infinite;
}

.fading-text-container .step-text:nth-child(1) {
  --index: 0;
  animation-delay: calc(var(--index) * var(--item-duration));
}

.fading-text-container .step-text:nth-child(2) {
  --index: 1;
  animation-delay: calc(var(--index) * var(--item-duration));
}

.fading-text-container .step-text:nth-child(3) {
  --index: 2;
  animation-delay: calc(var(--index) * var(--item-duration));
}

.fading-text-container .step-text:nth-child(4) {
  --index: 3;
  animation-delay: calc(var(--index) * var(--item-duration));
}

.fading-text-container .step-text:nth-child(5) {
  --index: 4;
  animation-delay: calc(var(--index) * var(--item-duration));
}

.fading-text-container .step-text:nth-child(6) {
  --index: 5;
  animation-delay: calc(var(--index) * var(--item-duration));
}
</style>
